import { Band, BandStack } from '@/components/ui/Band'
import { PageHero } from '@/components/ui/PageHero'
import { Eyebrow } from '@/components/ui/Section'
import { TraceLadder } from '@/components/ui/Illustrations'
import { IconBadge } from '@/components/ui/Icons'
import { ScrollRail } from '@/components/motion/ScrollRail'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'

const TOTAL_LAYERS = 4

const STAGES = [
  {
    label: 'Stage 1 · recorded',
    name: 'Source',
    icon: 'source' as const,
    tone: 'primary' as const,
    body: 'The document, identified: which file you sent, which sheet, which row range. Each source carries a label and a detail line so it is recognisable to the person who exported it.',
    note: 'Sources are listed per metric, not per dashboard. Two panels reading the same file for different reasons say so separately.',
  },
  {
    label: 'Stage 2 · recorded',
    name: 'Extraction',
    icon: 'extraction' as const,
    tone: 'primary' as const,
    body: 'The cell as it was written, and the value read out of it. Bracketed negatives, lakh grouping and a scale note three rows up all resolve here, and all three are shown as they were resolved.',
    note: 'This is the stage that catches a wrong figure most often. Nine times in ten a disputed number is a correct formula over a misread cell.',
  },
  {
    label: 'Stage 3 · computed',
    name: 'Calculation',
    icon: 'calculation' as const,
    tone: 'violet' as const,
    body: 'The formula with every measure substituted, so the arithmetic can be repeated on paper. Provenance is marked filed or derived: filed means it was stated in a document, derived means we computed it.',
    note: 'Drivers appear here with an explicit unattributed residual. What the decomposition cannot explain is shown rather than spread across the parts that can.',
  },
  {
    label: 'Stage 4 · computed',
    name: 'Model check',
    icon: 'check' as const,
    tone: 'violet' as const,
    body: 'How many points existed natively, how many the model trained on, how many were held back, and the error on that holdout. Also whether the history was sufficient at all — and if not, the trace ends here.',
    note: 'A trace that stops at stage four is a complete, honest trace. It means the figure is recorded and no projection was defensible.',
  },
  {
    label: 'Stage 5 · projected',
    name: 'Projection',
    icon: 'projection' as const,
    tone: 'projected' as const,
    body: 'The tier that answered, the decoder by name, whether the quantiles are native to the model or empirical from resampled paths, and the band itself.',
    note: 'The decoder name is stored with the forecast, not just displayed. A number produced by a fallback stays identifiable long after the run.',
  },
]

const ANSWER_FLOW = [
  {
    name: 'Answer produced',
    detail: 'Grounded in the same snapshot the panels were rendered from.',
  },
  {
    name: 'Figures checked',
    detail: 'Each number matched against the grounding, substitution included.',
  },
  {
    name: 'One retry',
    detail: 'A single regeneration, not a loop that eventually passes by chance.',
  },
  {
    name: 'Abstain',
    detail: 'No answer, and a reason. This is the designed outcome, not an error page.',
  },
]

export default function Provenance() {
  useDocumentMeta(ROUTES.provenance)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="provenance">
        <PageHero
          crumb="Provenance"
          title="A citation is a claim about a source. A trace is a record of the operation."
          lede="Most analytics tools can tell you which file a number came from. That is not the same as being able to reproduce it. Every figure here carries five ordered stages, each with its own rows, and each row keyed to the source it was read from."
        />
        <TraceLadder />
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="trace-stages-heading">
        <h2 id="trace-stages-heading" className="sr-only">
          The five trace stages
        </h2>
        <ScrollRail
          steps={STAGES.map((stage, index) => ({
            key: stage.name,
            badge: `S${index + 1}`,
            content: (
              <div className="card-spot grid gap-6 rounded-card border border-line bg-white/85 p-6 shadow-card backdrop-blur sm:p-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-10">
                <div>
                  <div className="flex items-center gap-4">
                    <IconBadge name={stage.icon} tone={stage.tone} />
                    <Eyebrow className="text-primary">{stage.label}</Eyebrow>
                  </div>
                  <h3 className="mt-4 text-[22px] font-extrabold tracking-[-0.02em] sm:text-[26px]">
                    {stage.name}
                  </h3>
                  <p className="mt-4 max-w-measure text-[14.5px] leading-[1.75] text-muted">
                    {stage.body}
                  </p>
                </div>
                <div className="rounded-card border border-line bg-surface-2/70 p-5 sm:p-6">
                  <p className="text-[13.5px] leading-[1.75] text-muted">{stage.note}</p>
                </div>
              </div>
            ),
          }))}
        />
      </Band>

      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="answers-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">Written answers</Eyebrow>
            <h2
              id="answers-heading"
              className="mt-6 text-balance text-[27px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
            >
              The no-fabrication rule is enforced mechanically, not by instruction.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              Before any written answer is shown, every number in it is checked against the
              grounding it was built from. The check is substitution-aware: it catches a figure that
              drifted from 153.2 to 158, not merely a missing citation.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              On failure the pipeline regenerates once, then abstains. Abstaining is a supported
              outcome with its own state in the interface, because the alternative is a confident
              wrong number.
            </p>
          </div>

          <div>
            <Stagger variant="right" step={90} as="ol" itemAs="li" className="grid gap-3">
              {ANSWER_FLOW.map((step, index) => (
                <div
                  key={step.name}
                  className="flex gap-4 rounded-card border border-line-dark bg-ink p-5 text-white shadow-card"
                  style={{ opacity: 0.82 + index * 0.045 }}
                >
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/10 font-mono text-[11px] text-white/80">
                    {index + 1}
                  </span>
                  <span className="min-w-0">
                    <strong className="block text-[14.5px] font-bold tracking-[-0.01em] text-white">
                      {step.name}
                    </strong>
                    <span className="mt-1 block text-[13.5px] leading-[1.65] text-white/70">
                      {step.detail}
                    </span>
                  </span>
                </div>
              ))}
            </Stagger>
            <p className="mt-5 text-[13.5px] leading-[1.7] text-muted-soft">
              The Ask surface is not built yet; the flag defaults off.
            </p>
          </div>
        </div>
      </Band>

      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="snapshot-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2
              id="snapshot-heading"
              className="text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[36px]"
            >
              One snapshot, one shape.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              The dashboard and any answer drawer read the same payload with the same types,
              generated from the server&rsquo;s own schemas. The moment two surfaces each own a copy
              of what a projection is, they start disagreeing.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              A field rename fails the build rather than silently rendering undefined into a figure,
              and a payload whose schema version differs from the client&rsquo;s is refused with a
              message rather than displayed.
            </p>
          </div>
          <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-8">
            <h3 className="text-[17px] font-extrabold tracking-[-0.015em]">
              Snapshots are dated and staleness is visible
            </h3>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              Every payload carries when it was generated, which snapshot it belongs to, and whether
              it is stale. A figure with no timestamp is a figure you cannot act on.
            </p>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              Two people looking at the same panel on different days can tell whether they are
              looking at the same reading.
            </p>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
